import { EXCLUDABLE_FILTER_FIELDS, type ExcludableFilterField } from '@ooh/contracts';

/**
 * Shared coercion helpers for catalog "sidecar" URL params (`noPhoto`, `exclude`).
 *
 * These pure functions are the single source of truth for turning raw,
 * untyped URL search values into the typed shapes used by both the route
 * search validator (`validate-search.ts`) and the sidecar param hooks
 * (`use-catalog-sidecar-params.ts`). Keeping them here (a `lib/` pure module)
 * avoids a `lib → hooks` dependency while removing the previously duplicated
 * coercion logic across those two sites.
 */

const EXCLUDABLE_FILTER_FIELD_SET = new Set<string>(EXCLUDABLE_FILTER_FIELDS);

/**
 * Coerces the URL `noPhoto` value to a boolean.
 * Truthy only for `true`, the string `"true"`, or the string `"1"`.
 */
export function coerceNoPhoto(raw: unknown): boolean {
  return raw === true || raw === 'true' || raw === '1';
}

/**
 * Coerces the URL `exclude` value to a list of valid excludable field names.
 * Accepts an array or a lone string, and drops any value that is not a known
 * `ExcludableFilterField`.
 */
export function coerceExclude(raw: unknown): ExcludableFilterField[] {
  const list = Array.isArray(raw) ? raw : typeof raw === 'string' && raw ? [raw] : [];
  return list.filter(
    (v): v is ExcludableFilterField => typeof v === 'string' && EXCLUDABLE_FILTER_FIELD_SET.has(v),
  );
}
